import type { HighlightStyle } from '../types/highlighter';

/**
 * Generate CSS for highlight overlay based on style type
 */
export function generateHighlightStyles(
  color: string,
  style: HighlightStyle,
  animationDuration: number
): string {
  const baseStyles = `
    position: absolute;
    pointer-events: none;
    box-sizing: border-box;
    transition: opacity ${animationDuration}ms ease-in-out;
  `;

  switch (style) {
    case 'solid':
      return `${baseStyles}
        border: 2px solid ${color};
        background-color: ${color}15;
      `;

    case 'dashed':
      return `${baseStyles}
        border: 2px dashed ${color};
        background-color: ${color}10;
      `;

    case 'pulse':
      return `${baseStyles}
        border: 2px solid ${color};
        background-color: ${color}15;
        animation: eazy-pulse ${animationDuration * 2}ms ease-in-out infinite;
      `;

    case 'glow':
      return `${baseStyles}
        border: 2px solid ${color};
        background-color: ${color}10;
        box-shadow: 0 0 10px ${color}, 0 0 20px ${color}80, inset 0 0 10px ${color}30;
        animation: eazy-glow ${animationDuration * 2}ms ease-in-out infinite;
      `;

    default:
      return baseStyles;
  }
}

/**
 * Generate CSS for label element
 */
export function generateLabelStyles(color: string): string {
  return `
    position: absolute;
    top: -24px;
    left: 0;
    padding: 4px 8px;
    background-color: ${color};
    color: white;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    font-size: 12px;
    font-weight: 500;
    border-radius: 3px;
    white-space: nowrap;
    z-index: 1;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  `;
}

/**
 * Inject CSS animations into document if not already present
 */
export function injectHighlighterAnimations(): void {
  const styleId = 'eazy-navigator-highlighter-animations';

  // Check if already injected
  if (document.getElementById(styleId)) {
    return;
  }

  const style = document.createElement('style');
  style.id = styleId;
  style.textContent = `
    @keyframes eazy-pulse {
      0%, 100% {
        opacity: 1;
        transform: scale(1);
      }
      50% {
        opacity: 0.7;
        transform: scale(1.02);
      }
    }

    @keyframes eazy-glow {
      0%, 100% {
        box-shadow: 0 0 10px currentColor, 0 0 20px currentColor, inset 0 0 10px currentColor;
      }
      50% {
        box-shadow: 0 0 20px currentColor, 0 0 40px currentColor, inset 0 0 20px currentColor;
      }
    }
  `;

  document.head.appendChild(style);
}

/**
 * Remove highlighter animations from document
 */
export function removeHighlighterAnimations(): void {
  const styleId = 'eazy-navigator-highlighter-animations';
  const style = document.getElementById(styleId);
  if (style) {
    style.remove();
  }
}
